/**
 * React hook for Meta Marketing API integration.
 *
 * Handles all API calls with snake_case ↔ camelCase transformation.
 * Uses useMarketingStore.getState() inside callbacks to avoid
 * stale closures and infinite re-render loops.
 */

import { useCallback } from 'react'
import { apiClient } from '@/services/apiClient'
import { useMarketingStore } from '@/stores/marketingStore'
import type {
  MetaAdAccount,
  MetaAccountConnectRequest,
  MetaAccountUpdateRequest,
  MetaAccountValidation,
  MarketingStatus,
  Campaign,
  CampaignCreateRequest,
  CampaignUpdateRequest,
  AdSet,
  AdSetCreateRequest,
  AdSetUpdateRequest,
  Ad,
  AdCreateRequest,
  AdUpdateRequest,
  ConversionEvent,
  ConversionEventCreateRequest,
  AnalyticsOverview,
  CampaignPerformanceRow,
  AttributionFunnelStep,
  TimeSeriesPoint,
  Audience,
  CustomAudienceCreateRequest,
  LookalikeAudienceCreateRequest,
  OptimizationAction,
  OptimizationActionReviewRequest,
  Creative,
  CreativeCreateRequest,
  CreativeVariationCreateRequest,
  MarketingChannel,
} from '@/types/marketing'

// ── Transform helpers (snake_case API → camelCase frontend) ──────────────

function transformAccount(raw: Record<string, unknown>): MetaAdAccount {
  return {
    id: raw.id as string,
    adAccountId: raw.ad_account_id as string,
    businessId: raw.business_id as string | undefined,
    name: raw.name as string | undefined,
    currency: (raw.currency as string) || 'USD',
    timezone: raw.timezone as string | undefined,
    pixelId: raw.pixel_id as string | undefined,
    pageId: raw.page_id as string | undefined,
    isActive: raw.is_active as boolean,
    syncStatus: (raw.sync_status as string) || 'pending',
    lastSyncedAt: raw.last_synced_at as string | undefined,
    syncError: raw.sync_error as string | undefined,
    hasAccessToken: (raw.has_access_token as boolean) ?? true,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  }
}

function transformCampaign(raw: Record<string, unknown>): Campaign {
  return {
    id: raw.id as string,
    metaAdAccountId: raw.meta_ad_account_id as string,
    metaCampaignId: (raw.meta_campaign_id as string) || '',
    name: raw.name as string,
    objective: (raw.objective as string) || 'OUTCOME_ENGAGEMENT',
    status: (raw.status as string) || 'PAUSED',
    dailyBudget: raw.daily_budget as number | undefined,
    lifetimeBudget: raw.lifetime_budget as number | undefined,
    bidStrategy: raw.bid_strategy as string | undefined,
    budgetOptimization: raw.budget_optimization as boolean,
    advantageAudience: raw.advantage_audience as boolean,
    advantagePlacement: raw.advantage_placement as boolean,
    startTime: raw.start_time as string | undefined,
    stopTime: raw.stop_time as string | undefined,
    personaId: raw.persona_id as string | undefined,
    tags: raw.tags as string[] | undefined,
    notes: raw.notes as string | undefined,
    syncStatus: (raw.sync_status as string) || 'pending',
    lastSyncedAt: raw.last_synced_at as string | undefined,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  }
}

function transformAdSet(raw: Record<string, unknown>): AdSet {
  return {
    id: raw.id as string,
    campaignId: raw.campaign_id as string,
    metaAdsetId: (raw.meta_ad_set_id as string) || '',
    name: raw.name as string,
    status: (raw.status as string) || 'PAUSED',
    dailyBudget: raw.daily_budget as number | undefined,
    lifetimeBudget: raw.lifetime_budget as number | undefined,
    minDailyBudget: raw.min_daily_budget as number | undefined,
    maxDailyBudget: raw.max_daily_budget as number | undefined,
    targeting: raw.targeting as Record<string, unknown> | undefined,
    optimizationGoal: raw.optimization_goal as string | undefined,
    billingEvent: raw.billing_event as string | undefined,
    isDynamicCreative: raw.is_dynamic_creative as boolean,
    startTime: raw.start_time as string | undefined,
    endTime: raw.end_time as string | undefined,
    lastSyncedAt: raw.last_synced_at as string | undefined,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  }
}

function transformAd(raw: Record<string, unknown>): Ad {
  return {
    id: raw.id as string,
    adSetId: raw.ad_set_id as string,
    metaAdId: (raw.meta_ad_id as string) || '',
    name: raw.name as string,
    status: (raw.status as string) || 'PAUSED',
    creative: raw.creative as Record<string, unknown> | undefined,
    approvalStatus: raw.approval_status as string | undefined,
    rejectionReason: raw.rejection_reason as string | undefined,
    lastSyncedAt: raw.last_synced_at as string | undefined,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  }
}

function transformConversion(raw: Record<string, unknown>): ConversionEvent {
  return {
    id: raw.id as string,
    metaAdAccountId: (raw.meta_ad_account_id as string) || '',
    conversationId: raw.conversation_id as string | undefined,
    contactId: raw.contact_id as string | undefined,
    eventName: raw.event_name as string,
    eventTime: raw.event_time as string,
    ctwaClid: raw.ctwa_clid as string | undefined,
    actionSource: (raw.action_source as string) || 'chat',
    messagingChannel: (raw.messaging_channel as string) || 'whatsapp',
    currency: raw.currency as string | undefined,
    value: raw.value as number | undefined,
    status: (raw.status as string) || 'pending',
    errorMessage: raw.error_message as string | undefined,
    sentAt: raw.sent_at as string | undefined,
    retryCount: (raw.retry_count as number) || 0,
    eventId: raw.event_id as string | undefined,
    createdAt: raw.created_at as string,
  }
}

function transformOverview(raw: Record<string, unknown>): AnalyticsOverview {
  return {
    totalCampaigns: (raw.total_campaigns as number) || 0,
    activeCampaigns: (raw.active_campaigns as number) || 0,
    totalSpend: (raw.total_spend as number) || 0,
    totalImpressions: (raw.total_impressions as number) || 0,
    totalClicks: (raw.total_clicks as number) || 0,
    totalConversations: (raw.total_conversations as number) || 0,
    totalLeads: (raw.total_leads as number) || 0,
    totalPurchases: (raw.total_purchases as number) || 0,
    totalPurchaseValue: (raw.total_purchase_value as number) || 0,
    avgCpc: (raw.avg_cpc as number) || 0,
    avgCtr: (raw.avg_ctr as number) || 0,
    avgCostPerConversation: (raw.avg_cost_per_conversation as number) || 0,
    avgCostPerLead: (raw.avg_cost_per_lead as number) || 0,
  }
}

function transformPerformanceRow(raw: Record<string, unknown>): CampaignPerformanceRow {
  return {
    campaignId: raw.campaign_id as string,
    channel: (raw.channel as MarketingChannel) || undefined,
    metaCampaignId: (raw.meta_campaign_id as string) || '',
    name: (raw.name || raw.campaign_name) as string,
    status: raw.status as string,
    objective: (raw.objective as string) || '',
    spend: (raw.spend as number) || 0,
    impressions: (raw.impressions as number) || 0,
    clicks: (raw.clicks as number) || 0,
    ctr: (raw.ctr as number) || 0,
    conversations: (raw.conversations as number) || 0,
    leads: (raw.leads as number) || 0,
    purchases: (raw.purchases as number) || 0,
    costPerConversation: (raw.cost_per_conversation as number) || 0,
    costPerLead: (raw.cost_per_lead as number) || 0,
  }
}

function transformFunnelStep(raw: Record<string, unknown>): AttributionFunnelStep {
  return {
    label: raw.label as string,
    count: (raw.count as number) || 0,
    cost: (raw.cost as number) || 0,
    untracked: raw.untracked === true,
  }
}

function transformTimeSeriesPoint(raw: Record<string, unknown>): TimeSeriesPoint {
  return {
    date: raw.date as string,
    spend: (raw.spend as number) || 0,
    impressions: (raw.impressions as number) || 0,
    clicks: (raw.clicks as number) || 0,
    conversations: (raw.conversations as number) || 0,
    leads: (raw.leads as number) || 0,
  }
}

function transformAudience(raw: Record<string, unknown>): Audience {
  return {
    id: raw.id as string,
    metaAdAccountId: raw.meta_ad_account_id as string,
    name: raw.name as string,
    audienceType: (raw.audience_type as string) || 'custom',
    sourceType: raw.source_type as string | undefined,
    sourceConfig: raw.source_config as Record<string, unknown> | undefined,
    metaAudienceId: raw.meta_audience_id as string | undefined,
    status: (raw.status as string) || 'building',
    approximateCount: raw.approximate_count as number | undefined,
    seedAudienceId: raw.seed_audience_id as string | undefined,
    lookalikeCountry: raw.lookalike_country as string | undefined,
    lookalikeRatio: raw.lookalike_ratio as number | undefined,
    lastSyncedAt: raw.last_synced_at as string | undefined,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  }
}

function transformOptimizationAction(raw: Record<string, unknown>): OptimizationAction {
  return {
    id: raw.id as string,
    campaignId: raw.campaign_id as string,
    actionType: raw.action_type as string,
    entityType: (raw.entity_type as string) || 'campaign',
    entityId: (raw.entity_id as string) || '',
    description: raw.description as string | undefined,
    beforeState: raw.before_state as Record<string, unknown> | undefined,
    afterState: raw.after_state as Record<string, unknown> | undefined,
    confidence: (raw.confidence as number) || 0,
    status: (raw.status as string) || 'recommended',
    recommendedAt: raw.recommended_at as string | undefined,
    reviewedBy: raw.reviewed_by as string | undefined,
    reviewedAt: raw.reviewed_at as string | undefined,
    executedAt: raw.executed_at as string | undefined,
    createdAt: raw.created_at as string,
  }
}

function transformCreative(raw: Record<string, unknown>): Creative {
  return {
    id: raw.id as string,
    metaAdAccountId: raw.meta_ad_account_id as string,
    name: raw.name as string,
    headline: raw.headline as string | undefined,
    bodyText: raw.body_text as string | undefined,
    callToAction: raw.call_to_action as string | undefined,
    fileFormat: raw.file_format as string | undefined,
    fileSizeBytes: raw.file_size_bytes as number | undefined,
    width: raw.width as number | undefined,
    height: raw.height as number | undefined,
    blobUrl: raw.blob_url as string | undefined,
    thumbnailUrl: raw.thumbnail_url as string | undefined,
    metaImageHash: raw.meta_image_hash as string | undefined,
    metaCreativeId: raw.meta_creative_id as string | undefined,
    parentCreativeId: raw.parent_creative_id as string | undefined,
    metadata: raw.metadata as Record<string, unknown> | undefined,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  }
}

// ── Serializers (camelCase frontend → snake_case API) ─────────────────

function serializeCampaignCreate(data: CampaignCreateRequest): Record<string, unknown> {
  return {
    name: data.name,
    objective: data.objective,
    status: data.status,
    daily_budget: data.dailyBudget,
    lifetime_budget: data.lifetimeBudget,
    bid_strategy: data.bidStrategy,
    budget_optimization: data.budgetOptimization,
    advantage_audience: data.advantageAudience,
    advantage_placement: data.advantagePlacement,
    start_time: data.startTime,
    stop_time: data.stopTime,
    persona_id: data.personaId,
    tags: data.tags,
    notes: data.notes,
  }
}

function serializeCampaignUpdate(data: CampaignUpdateRequest): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  if (data.name !== undefined) result.name = data.name
  if (data.status !== undefined) result.status = data.status
  if (data.dailyBudget !== undefined) result.daily_budget = data.dailyBudget
  if (data.lifetimeBudget !== undefined) result.lifetime_budget = data.lifetimeBudget
  if (data.bidStrategy !== undefined) result.bid_strategy = data.bidStrategy
  if (data.budgetOptimization !== undefined) result.budget_optimization = data.budgetOptimization
  if (data.advantageAudience !== undefined) result.advantage_audience = data.advantageAudience
  if (data.advantagePlacement !== undefined) result.advantage_placement = data.advantagePlacement
  if (data.startTime !== undefined) result.start_time = data.startTime
  if (data.stopTime !== undefined) result.stop_time = data.stopTime
  if (data.personaId !== undefined) result.persona_id = data.personaId
  if (data.tags !== undefined) result.tags = data.tags
  if (data.notes !== undefined) result.notes = data.notes
  return result
}

function serializeAdSetCreate(data: AdSetCreateRequest): Record<string, unknown> {
  return {
    name: data.name,
    status: data.status,
    daily_budget: data.dailyBudget,
    lifetime_budget: data.lifetimeBudget,
    min_daily_budget: data.minDailyBudget,
    max_daily_budget: data.maxDailyBudget,
    targeting: data.targeting,
    optimization_goal: data.optimizationGoal,
    billing_event: data.billingEvent,
    is_dynamic_creative: data.isDynamicCreative,
    start_time: data.startTime,
    end_time: data.endTime,
  }
}

function serializeAdSetUpdate(data: AdSetUpdateRequest): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  if (data.name !== undefined) result.name = data.name
  if (data.status !== undefined) result.status = data.status
  if (data.dailyBudget !== undefined) result.daily_budget = data.dailyBudget
  if (data.lifetimeBudget !== undefined) result.lifetime_budget = data.lifetimeBudget
  if (data.minDailyBudget !== undefined) result.min_daily_budget = data.minDailyBudget
  if (data.maxDailyBudget !== undefined) result.max_daily_budget = data.maxDailyBudget
  if (data.targeting !== undefined) result.targeting = data.targeting
  if (data.optimizationGoal !== undefined) result.optimization_goal = data.optimizationGoal
  if (data.billingEvent !== undefined) result.billing_event = data.billingEvent
  if (data.isDynamicCreative !== undefined) result.is_dynamic_creative = data.isDynamicCreative
  if (data.startTime !== undefined) result.start_time = data.startTime
  if (data.endTime !== undefined) result.end_time = data.endTime
  return result
}

function serializeAccountConnect(data: MetaAccountConnectRequest): Record<string, unknown> {
  return {
    access_token: data.accessToken,
    ad_account_id: data.adAccountId,
    pixel_id: data.pixelId,
    page_id: data.pageId,
  }
}

function serializeAccountUpdate(data: MetaAccountUpdateRequest): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  if (data.pixelId !== undefined) result.pixel_id = data.pixelId
  if (data.pageId !== undefined) result.page_id = data.pageId
  if (data.isActive !== undefined) result.is_active = data.isActive
  return result
}

// ── Helpers ───────────────────────────────────────────────────────────

/** Access store state without creating a render dependency. */
const getState = () => useMarketingStore.getState()

// ── Main hook ────────────────────────────────────────────────────────────

export function useMarketing() {
  // Subscribe to store for reading state only — callbacks use getState()
  const account = useMarketingStore((s) => s.account)
  const hasAccount = useMarketingStore((s) => s.hasAccount)
  const campaigns = useMarketingStore((s) => s.campaigns)
  const adSets = useMarketingStore((s) => s.adSets)
  const ads = useMarketingStore((s) => s.ads)
  const overview = useMarketingStore((s) => s.overview)
  const campaignPerformance = useMarketingStore((s) => s.campaignPerformance)
  const attributionFunnel = useMarketingStore((s) => s.attributionFunnel)
  const attributionUnattributed = useMarketingStore((s) => s.attributionUnattributed)
  const timeSeries = useMarketingStore((s) => s.timeSeries)
  const conversionEvents = useMarketingStore((s) => s.conversionEvents)
  const conversionTotal = useMarketingStore((s) => s.conversionTotal)
  const audiences = useMarketingStore((s) => s.audiences)
  const optimizationActions = useMarketingStore((s) => s.optimizationActions)
  const creatives = useMarketingStore((s) => s.creatives)
  const isLoading = useMarketingStore((s) => s.isLoading)

  // ── Account ──

  const fetchStatus = useCallback(async (): Promise<MarketingStatus> => {
    const res = await apiClient.fetch('/api/marketing/status')
    if (!res.ok) throw new Error('Failed to fetch marketing status')
    const data = await res.json()
    const status: MarketingStatus = {
      hasAccount: data.has_account,
      account: data.account ? transformAccount(data.account) : undefined,
      accounts: data.accounts || [],
      connectedChannels: data.connected_channels || [],
      requiredPermissions: data.required_permissions || [],
    }
    const s = getState()
    s.setHasAccount(status.hasAccount)
    if (status.account) s.setAccount(status.account)
    s.setConnectedChannels(status.connectedChannels)
    if (status.accounts.length > 0) s.setAdAccounts(status.accounts)
    return status
  }, [])

  const validateToken = useCallback(async (data: MetaAccountConnectRequest): Promise<MetaAccountValidation> => {
    const res = await apiClient.fetch('/api/marketing/validate-token', {
      method: 'POST',
      body: JSON.stringify(serializeAccountConnect(data)),
    })
    if (!res.ok) throw new Error('Token validation failed')
    const raw = await res.json()
    return {
      valid: raw.valid,
      userId: raw.user_id,
      userName: raw.user_name,
      adAccounts: raw.ad_accounts || [],
      permissions: raw.permissions || [],
      missingPermissions: raw.missing_permissions || [],
    }
  }, [])

  const connectAccount = useCallback(async (data: MetaAccountConnectRequest): Promise<MetaAdAccount> => {
    const res = await apiClient.fetch('/api/marketing/account', {
      method: 'POST',
      body: JSON.stringify(serializeAccountConnect(data)),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error((err as Record<string, string>).detail || 'Failed to connect account')
    }
    const acct = transformAccount(await res.json())
    const s = getState()
    s.setAccount(acct)
    s.setHasAccount(true)
    return acct
  }, [])

  const updateAccount = useCallback(async (data: MetaAccountUpdateRequest): Promise<MetaAdAccount> => {
    const res = await apiClient.fetch('/api/marketing/account', {
      method: 'PATCH',
      body: JSON.stringify(serializeAccountUpdate(data)),
    })
    if (!res.ok) throw new Error('Failed to update account')
    const acct = transformAccount(await res.json())
    getState().setAccount(acct)
    return acct
  }, [])

  const disconnectAccount = useCallback(async (): Promise<void> => {
    const res = await apiClient.fetch('/api/marketing/account', { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to disconnect account')
    const s = getState()
    s.setAccount(null)
    s.setHasAccount(false)
  }, [])

  // ── Campaigns ──

  const syncCampaigns = useCallback(async (): Promise<{ created: number; updated: number; total: number }> => {
    const ch = getState().selectedChannel
    // Dispatch to the correct sync endpoint based on selected channel
    const syncUrl = ch === 'google'
      ? '/api/marketing/google/sync'
      : '/api/marketing/campaigns/sync'
    const res = await apiClient.fetch(syncUrl, { method: 'POST' })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error((err as Record<string, string>).detail || 'Failed to sync campaigns')
    }
    return res.json()
  }, [])

  const fetchCampaigns = useCallback(async (): Promise<Campaign[]> => {
    const ch = getState().selectedChannel
    const params = ch ? `?channel=${ch}` : ''
    const res = await apiClient.fetch(`/api/marketing/campaigns${params}`)
    if (!res.ok) throw new Error('Failed to fetch campaigns')
    const data = await res.json()
    const result = (data.campaigns || []).map((r: Record<string, unknown>) => transformCampaign(r))
    getState().setCampaigns(result)
    return result
  }, [])

  const createCampaign = useCallback(async (data: CampaignCreateRequest): Promise<Campaign> => {
    const res = await apiClient.fetch('/api/marketing/campaigns', {
      method: 'POST',
      body: JSON.stringify(serializeCampaignCreate(data)),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error((err as Record<string, string>).detail || 'Failed to create campaign')
    }
    const campaign = transformCampaign(await res.json())
    const s = getState()
    s.setCampaigns([...s.campaigns, campaign])
    return campaign
  }, [])

  const updateCampaign = useCallback(async (id: string, data: CampaignUpdateRequest): Promise<Campaign> => {
    const res = await apiClient.fetch(`/api/marketing/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serializeCampaignUpdate(data)),
    })
    if (!res.ok) throw new Error('Failed to update campaign')
    const campaign = transformCampaign(await res.json())
    const s = getState()
    s.setCampaigns(s.campaigns.map((c) => (c.id === id ? campaign : c)))
    return campaign
  }, [])

  const deleteCampaign = useCallback(async (id: string): Promise<void> => {
    const res = await apiClient.fetch(`/api/marketing/campaigns/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete campaign')
    const s = getState()
    s.setCampaigns(s.campaigns.filter((c) => c.id !== id))
  }, [])

  const pauseCampaign = useCallback(async (id: string): Promise<Campaign> => {
    const res = await apiClient.fetch(`/api/marketing/campaigns/${id}/pause`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to pause campaign')
    const campaign = transformCampaign(await res.json())
    const s = getState()
    s.setCampaigns(s.campaigns.map((c) => (c.id === id ? campaign : c)))
    return campaign
  }, [])

  const activateCampaign = useCallback(async (id: string): Promise<Campaign> => {
    const res = await apiClient.fetch(`/api/marketing/campaigns/${id}/activate`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to activate campaign')
    const campaign = transformCampaign(await res.json())
    const s = getState()
    s.setCampaigns(s.campaigns.map((c) => (c.id === id ? campaign : c)))
    return campaign
  }, [])

  // ── Ad Sets ──

  const fetchAdSets = useCallback(async (campaignId: string): Promise<AdSet[]> => {
    const res = await apiClient.fetch(`/api/marketing/campaigns/${campaignId}/ad-sets`)
    if (!res.ok) throw new Error('Failed to fetch ad sets')
    const data = await res.json()
    const result = (Array.isArray(data) ? data : []).map((r: Record<string, unknown>) => transformAdSet(r))
    getState().setAdSets(result)
    return result
  }, [])

  const createAdSet = useCallback(async (campaignId: string, data: AdSetCreateRequest): Promise<AdSet> => {
    const res = await apiClient.fetch(`/api/marketing/campaigns/${campaignId}/ad-sets`, {
      method: 'POST',
      body: JSON.stringify(serializeAdSetCreate(data)),
    })
    if (!res.ok) throw new Error('Failed to create ad set')
    const adSet = transformAdSet(await res.json())
    const s = getState()
    s.setAdSets([...s.adSets, adSet])
    return adSet
  }, [])

  const updateAdSet = useCallback(async (adSetId: string, data: AdSetUpdateRequest): Promise<AdSet> => {
    const res = await apiClient.fetch(`/api/marketing/ad-sets/${adSetId}`, {
      method: 'PUT',
      body: JSON.stringify(serializeAdSetUpdate(data)),
    })
    if (!res.ok) throw new Error('Failed to update ad set')
    const adSet = transformAdSet(await res.json())
    const s = getState()
    s.setAdSets(s.adSets.map((a) => (a.id === adSetId ? adSet : a)))
    return adSet
  }, [])

  // ── Ads ──

  const fetchAds = useCallback(async (adSetId: string): Promise<Ad[]> => {
    const res = await apiClient.fetch(`/api/marketing/ad-sets/${adSetId}/ads`)
    if (!res.ok) throw new Error('Failed to fetch ads')
    const data = await res.json()
    const result = (Array.isArray(data) ? data : []).map((r: Record<string, unknown>) => transformAd(r))
    getState().setAds(result)
    return result
  }, [])

  const createAd = useCallback(async (adSetId: string, data: AdCreateRequest): Promise<Ad> => {
    const res = await apiClient.fetch(`/api/marketing/ad-sets/${adSetId}/ads`, {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        status: data.status,
        creative: data.creative,
      }),
    })
    if (!res.ok) throw new Error('Failed to create ad')
    const ad = transformAd(await res.json())
    const s = getState()
    s.setAds([...s.ads, ad])
    return ad
  }, [])

  const updateAd = useCallback(async (adId: string, data: AdUpdateRequest): Promise<Ad> => {
    const res = await apiClient.fetch(`/api/marketing/ads/${adId}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: data.name,
        status: data.status,
        creative: data.creative,
      }),
    })
    if (!res.ok) throw new Error('Failed to update ad')
    const ad = transformAd(await res.json())
    const s = getState()
    s.setAds(s.ads.map((a) => (a.id === adId ? ad : a)))
    return ad
  }, [])

  // ── Conversions ──

  const fetchConversions = useCallback(async (status?: string): Promise<void> => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    const ch = getState().selectedChannel
    if (ch) params.set('channel', ch)
    const qs = params.toString()
    const res = await apiClient.fetch(`/api/marketing/conversions${qs ? `?${qs}` : ''}`)
    if (!res.ok) throw new Error('Failed to fetch conversions')
    const data = await res.json()
    const events = (data.events || []).map((r: Record<string, unknown>) => transformConversion(r))
    getState().setConversionEvents(events, data.total || 0)
  }, [])

  const recordConversion = useCallback(async (data: ConversionEventCreateRequest): Promise<ConversionEvent> => {
    const res = await apiClient.fetch('/api/marketing/conversions', {
      method: 'POST',
      body: JSON.stringify({
        event_name: data.eventName,
        conversation_id: data.conversationId,
        contact_id: data.contactId,
        value: data.value,
        currency: data.currency,
      }),
    })
    if (!res.ok) throw new Error('Failed to record conversion')
    return transformConversion(await res.json())
  }, [])

  const dispatchConversions = useCallback(async (): Promise<number> => {
    const res = await apiClient.fetch('/api/marketing/conversions/dispatch', { method: 'POST' })
    if (!res.ok) throw new Error('Failed to dispatch conversions')
    const data = await res.json()
    return data.dispatched || 0
  }, [])

  const retryConversions = useCallback(async (): Promise<number> => {
    const res = await apiClient.fetch('/api/marketing/conversions/retry', { method: 'POST' })
    if (!res.ok) throw new Error('Failed to retry conversions')
    const data = await res.json()
    return data.reset_for_retry || 0
  }, [])

  // ── Analytics ──

  /** Build query string with optional channel filter from store. */
  const getChannelParam = () => {
    const ch = getState().selectedChannel
    return ch ? `&channel=${ch}` : ''
  }

  /** Repeatable campaign_ids params from the shared selection — ANDs with
   * days + channel on the backend (overview/time-series/attribution only;
   * /analytics/campaigns takes no campaign filter by design). */
  const getCampaignIdsParam = () => {
    return getState().selectedCampaignIds
      .map((id) => `&campaign_ids=${encodeURIComponent(id)}`)
      .join('')
  }

  const fetchOverview = useCallback(async (days = 30): Promise<AnalyticsOverview> => {
    const res = await apiClient.fetch(`/api/marketing/analytics/overview?days=${days}${getChannelParam()}${getCampaignIdsParam()}`)
    if (!res.ok) throw new Error('Failed to fetch analytics overview')
    const result = transformOverview(await res.json())
    getState().setOverview(result)
    return result
  }, [])

  const fetchCampaignPerformance = useCallback(async (days = 30): Promise<CampaignPerformanceRow[]> => {
    const res = await apiClient.fetch(`/api/marketing/analytics/campaigns?days=${days}${getChannelParam()}`)
    if (!res.ok) throw new Error('Failed to fetch campaign performance')
    const data = await res.json()
    const rows = (data.campaigns || []).map((r: Record<string, unknown>) => transformPerformanceRow(r))
    getState().setCampaignPerformance(rows)
    return rows
  }, [])

  const fetchAttribution = useCallback(async (days = 30): Promise<AttributionFunnelStep[]> => {
    const res = await apiClient.fetch(`/api/marketing/analytics/attribution?days=${days}${getChannelParam()}${getCampaignIdsParam()}`)
    if (!res.ok) throw new Error('Failed to fetch attribution funnel')
    const data = await res.json()
    const steps = (data.steps || []).map((r: Record<string, unknown>) => transformFunnelStep(r))
    getState().setAttributionFunnel(steps)
    // Present only on the unfiltered view; null = annotation not applicable.
    getState().setAttributionUnattributed(
      typeof data.unattributed_purchases === 'number' ? data.unattributed_purchases : null,
    )
    return steps
  }, [])

  const fetchTimeSeries = useCallback(async (days = 30): Promise<TimeSeriesPoint[]> => {
    const res = await apiClient.fetch(`/api/marketing/analytics/time-series?days=${days}${getChannelParam()}${getCampaignIdsParam()}`)
    if (!res.ok) throw new Error('Failed to fetch time series')
    const data = await res.json()
    const points = (data.points || []).map((r: Record<string, unknown>) => transformTimeSeriesPoint(r))
    getState().setTimeSeries(points)
    return points
  }, [])

  const syncInsights = useCallback(async (): Promise<void> => {
    const cp = getChannelParam()
    const res = await apiClient.fetch(`/api/marketing/analytics/sync${cp ? '?' + cp.slice(1) : ''}`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to sync insights')
  }, [])

  // ── Cross-Channel Analytics ──

  const fetchChannelComparison = useCallback(async (days = 30) => {
    const res = await apiClient.fetch(`/api/marketing/analytics/channel-comparison?days=${days}`)
    if (!res.ok) throw new Error('Failed to fetch channel comparison')
    return await res.json()
  }, [])

  const fetchBlendedKPIs = useCallback(async (days = 30) => {
    const res = await apiClient.fetch(`/api/marketing/analytics/blended-kpis?days=${days}`)
    if (!res.ok) throw new Error('Failed to fetch blended KPIs')
    return await res.json()
  }, [])

  const fetchSpendEfficiency = useCallback(async (days = 30) => {
    const res = await apiClient.fetch(`/api/marketing/analytics/spend-efficiency?days=${days}`)
    if (!res.ok) throw new Error('Failed to fetch spend efficiency')
    return await res.json()
  }, [])

  // ── Audiences ──

  const syncAudiences = useCallback(async (): Promise<{ created: number }> => {
    const res = await apiClient.fetch('/api/marketing/audiences/sync', { method: 'POST' })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error((err as Record<string, string>).detail || 'Failed to sync audiences')
    }
    return res.json()
  }, [])

  const fetchAudiences = useCallback(async (): Promise<Audience[]> => {
    const res = await apiClient.fetch('/api/marketing/audiences')
    if (!res.ok) throw new Error('Failed to fetch audiences')
    const data = await res.json()
    const result = (Array.isArray(data) ? data : []).map((r: Record<string, unknown>) => transformAudience(r))
    getState().setAudiences(result)
    return result
  }, [])

  const createCustomAudience = useCallback(async (data: CustomAudienceCreateRequest): Promise<Audience> => {
    const res = await apiClient.fetch('/api/marketing/audiences/custom', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        source_type: data.sourceType,
        source_config: data.sourceConfig,
      }),
    })
    if (!res.ok) throw new Error('Failed to create custom audience')
    const audience = transformAudience(await res.json())
    const s = getState()
    s.setAudiences([...s.audiences, audience])
    return audience
  }, [])

  const createLookalikeAudience = useCallback(async (data: LookalikeAudienceCreateRequest): Promise<Audience> => {
    const res = await apiClient.fetch('/api/marketing/audiences/lookalike', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        seed_audience_id: data.seedAudienceId,
        country: data.country,
        ratio: data.ratio,
      }),
    })
    if (!res.ok) throw new Error('Failed to create lookalike audience')
    const audience = transformAudience(await res.json())
    const s = getState()
    s.setAudiences([...s.audiences, audience])
    return audience
  }, [])

  const syncAudience = useCallback(async (id: string): Promise<number> => {
    const res = await apiClient.fetch(`/api/marketing/audiences/${id}/sync`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to sync audience')
    const data = await res.json()
    return data.synced_contacts || 0
  }, [])

  const deleteAudience = useCallback(async (id: string): Promise<void> => {
    const res = await apiClient.fetch(`/api/marketing/audiences/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete audience')
    const s = getState()
    s.setAudiences(s.audiences.filter((a) => a.id !== id))
  }, [])

  // ── Optimization ──

  const fetchOptimizationActions = useCallback(async (status?: string, campaignId?: string): Promise<OptimizationAction[]> => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (campaignId) params.set('campaign_id', campaignId)
    const qs = params.toString()
    const res = await apiClient.fetch(`/api/marketing/optimization/actions${qs ? `?${qs}` : ''}`)
    if (!res.ok) throw new Error('Failed to fetch optimization actions')
    const data = await res.json()
    const actions = (Array.isArray(data) ? data : []).map((r: Record<string, unknown>) => transformOptimizationAction(r))
    getState().setOptimizationActions(actions)
    return actions
  }, [])

  const reviewOptimizationAction = useCallback(async (id: string, review: OptimizationActionReviewRequest): Promise<OptimizationAction> => {
    const res = await apiClient.fetch(`/api/marketing/optimization/actions/${id}/review`, {
      method: 'POST',
      body: JSON.stringify({ action: review.action }),
    })
    if (!res.ok) throw new Error('Failed to review optimization action')
    const action = transformOptimizationAction(await res.json())
    const s = getState()
    s.setOptimizationActions(s.optimizationActions.map((a) => (a.id === id ? action : a)))
    return action
  }, [])

  const runOptimization = useCallback(async (campaignId?: string): Promise<number> => {
    const url = campaignId
      ? `/api/marketing/optimization/run?campaign_id=${encodeURIComponent(campaignId)}`
      : '/api/marketing/optimization/run'
    const res = await apiClient.fetch(url, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to run optimization')
    const data = await res.json()
    return data.recommendations || 0
  }, [])

  // ── Creatives ──

  const fetchCreatives = useCallback(async (): Promise<Creative[]> => {
    const res = await apiClient.fetch('/api/marketing/creatives')
    if (!res.ok) throw new Error('Failed to fetch creatives')
    const data = await res.json()
    const result = (Array.isArray(data) ? data : []).map((r: Record<string, unknown>) => transformCreative(r))
    getState().setCreatives(result)
    return result
  }, [])

  const createCreative = useCallback(async (data: CreativeCreateRequest): Promise<Creative> => {
    const res = await apiClient.fetch('/api/marketing/creatives', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        headline: data.headline,
        body_text: data.bodyText,
        call_to_action: data.callToAction,
        metadata: data.metadata,
      }),
    })
    if (!res.ok) throw new Error('Failed to create creative')
    const creative = transformCreative(await res.json())
    const s = getState()
    s.setCreatives([...s.creatives, creative])
    return creative
  }, [])

  const createVariation = useCallback(async (creativeId: string, data: CreativeVariationCreateRequest): Promise<Creative> => {
    const res = await apiClient.fetch(`/api/marketing/creatives/${creativeId}/variations`, {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        headline: data.headline,
        body_text: data.bodyText,
        call_to_action: data.callToAction,
      }),
    })
    if (!res.ok) throw new Error('Failed to create creative variation')
    const creative = transformCreative(await res.json())
    const s = getState()
    s.setCreatives([...s.creatives, creative])
    return creative
  }, [])

  const uploadCreativeImage = useCallback(async (file: File): Promise<{ imageHash: string; filename: string; sizeBytes: number }> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await apiClient.fetch('/api/marketing/creatives/upload-image', {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error((err as Record<string, string>).detail || 'Failed to upload image')
    }
    const data = await res.json()
    return {
      imageHash: data.image_hash as string,
      filename: data.filename as string,
      sizeBytes: data.size_bytes as number,
    }
  }, [])

  const deleteCreative = useCallback(async (id: string): Promise<void> => {
    const res = await apiClient.fetch(`/api/marketing/creatives/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete creative')
    const s = getState()
    s.setCreatives(s.creatives.filter((c) => c.id !== id))
  }, [])

  return {
    // State (subscribed via selectors)
    account,
    hasAccount,
    campaigns,
    adSets,
    ads,
    overview,
    campaignPerformance,
    attributionFunnel,
    attributionUnattributed,
    timeSeries,
    conversionEvents,
    conversionTotal,
    audiences,
    optimizationActions,
    creatives,
    isLoading,

    // Account
    fetchStatus,
    validateToken,
    connectAccount,
    updateAccount,
    disconnectAccount,

    // Campaigns
    syncCampaigns,
    fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    pauseCampaign,
    activateCampaign,

    // Ad Sets
    fetchAdSets,
    createAdSet,
    updateAdSet,

    // Ads
    fetchAds,
    createAd,
    updateAd,

    // Conversions
    fetchConversions,
    recordConversion,
    dispatchConversions,
    retryConversions,

    // Analytics
    fetchOverview,
    fetchCampaignPerformance,
    fetchAttribution,
    fetchTimeSeries,
    syncInsights,

    // Cross-Channel Analytics
    fetchChannelComparison,
    fetchBlendedKPIs,
    fetchSpendEfficiency,

    // Audiences
    syncAudiences,
    fetchAudiences,
    createCustomAudience,
    createLookalikeAudience,
    syncAudience,
    deleteAudience,

    // Optimization
    fetchOptimizationActions,
    reviewOptimizationAction,
    runOptimization,

    // Creatives
    uploadCreativeImage,
    fetchCreatives,
    createCreative,
    createVariation,
    deleteCreative,
  }
}
